import React, { useEffect, useState } from 'react';
import { Table, TextInput, rem, createStyles, Text, Center, Pagination, Flex, ScrollArea, Button, Skeleton, Title, Box, Modal, Radio } from '@mantine/core';
import { IconSearch, IconDownload, IconPencil } from '@tabler/icons-react';
import { keys } from '@mantine/utils';
import { getUsers } from '../../services/api.service';
import { notifications } from '@mantine/notifications';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import dayjs from 'dayjs';
import { updateUserPoints } from '../../services/api.service';
import { useForm } from '@mantine/form';
// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: theme.spacing.md,
		height: '100%',
	},
	th: {
		padding: '0 !important',
	},
	control: {
		width: '100%',
		padding: `${theme.spacing.xs} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[1],
		},
	},
	icon: {
		width: rem(21),
		height: rem(21),
		borderRadius: rem(21),
	},
}));

const Users = () => {

	// State and hooks
	const [isLoading, setIsLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [allUsers, setAllUsers] = useState([]);
	const [users, setUsers] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const { classes } = useStyles();
	const [total, setTotal] = useState(0);
	const [limit] = useState(10);
	const [page, setPage] = useState(1);
	const [opened, setOpened] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState(null);
	const [pointsMode, setPointsMode] = useState('sumar');
	const [isSaving, setIsSaving] = useState(false);
	const form = useForm({
		initialValues: {
		  message: {
			subject: '',
			body: ''
		  },
		  points: 0,
		},
	});
	  
	const offset = page - 1;

	// On load
	useEffect(() => {
		refreshAllUsers();
		getUsersList();
		// eslint-disable-next-line
	}, []);
	
	// Get beta users
	const getUsersList = () => {

		// Get
		getUsers(`?limit=${limit}&offset=${offset}`, function (response) {

			// Set beta users and initial sorted data, remove loader
			setUsers(response.data.data);
			setFilteredData(response.data.data);
			setTotal(response.data.total);
			setIsLoading(false);

		}, function (error) {

			// Remove loader
			setIsLoading(false);

			// Show error
			notifications.show({
				title: 'Error',
				message: error,
				color: 'red'
			});

		});

	}

	// TH component
	const Th = ({ children, width }) => {
		const { classes } = useStyles();
		return (
			<th className={classes.th} width={width}>
				<Text
					fw={700}
					fz="sm"
					className={classes.control}
				>
					{children}
				</Text>
			</th>
		);
	}

	// Build table head
	const ths = (
		<tr>
			<Th>
				ID
			</Th>
			<Th>
				Nombre
			</Th>
			<Th>
				Apellido
			</Th>
			<Th>
				Correo Electrónico
			</Th>
			<Th>
				Sistema Operativo
			</Th>
			<Th>
				Nivel
			</Th>
			<Th>
				Wepoints
			</Th>
			<Th>
				Género
			</Th>
			<Th>
				Tipo de Usuario
			</Th>
			<Th>
				Fecha de Registro
			</Th>
			<Th>
				Último Inicio de Sesión
			</Th>
		</tr>
	);

	const refreshAllUsers = () => {
		getUsers(`?limit=9999&offset=0`, (response) => {
			const fresh = response.data.data;
			setAllUsers(fresh);
	
			if (search.trim()) {
				const query = search.toLowerCase().trim();
				const filtered = fresh.filter((item) =>
					Object.keys(fresh[0] || {}).some((key) =>
						String(item[key] ?? '').toLowerCase().includes(query)
					)
				);
				setFilteredData(filtered);
				setPage(1);
			}
		});
	};	

	// Build rows
	const dataToShow = search.trim()
		? filteredData.slice((page - 1) * limit, page * limit)
		: users;

	const rows = dataToShow.map((element) => (
		<tr key={element.id}>
			<td width={100}>
				{element.id}
			</td>
			<td>
				{element.name}
			</td>
			<td>
				{element.lastName}
			</td>
			<td>
				{element.email}
			</td>
			<td>
				{element.os ? element.os : '-'}
			</td>
			<td>
				{element.level ? `(${element.level.id}) ${element.level.name}` : ''}
			</td>
			<td>
				{element.totalPoints ? element.totalPoints : '0'}
				<IconPencil
				  size={16}
				  color="#4A0382"
				  style={{ marginLeft: 5, cursor: 'pointer' }}
				  onClick={() => {
					setSelectedUserId(element.id);
					setOpened(true);
				  }}				  
				/>
			</td>
			<td>
				{element.gender ? element.gender.name : ''}
			</td>
			<td>
				{element.userType ? element.userType.name : ''}
			</td>
			<td>
				{dayjs(new Date(element.createdAt)).format('DD-MM-YYYY HH:mm')}
			</td>
			<td>
				{dayjs(new Date(element.lastLoginAt)).format('DD-MM-YYYY HH:mm')}
			</td>
		</tr>
	));

	// Handle search change
	const handleSearchChange = (event) => {
		const { value } = event.currentTarget;
		setSearch(value);
	
		if (value.trim()) {
			const query = value.toLowerCase().trim();
			const filtered = allUsers.filter((item) =>
				keys(allUsers[0]).some((key) =>
					item[key]?.toString().toLowerCase().includes(query)
				)
			);
			setFilteredData(filtered);
			setPage(1);
		} else {
			setFilteredData([]);
			setPage(1);
		}
	};

	// Filter and return from query/search/sort
	const filterData = (data, search) => {
		const query = search.toLowerCase().trim();
		setFilteredData(data.filter((item) => keys(data[0]).some((key) => item[key]?.toString().toLowerCase().includes(query))));
	}

	// Convert and download as CSV file
	const convertToCSV = () => {
		// Use filtered data if search is active, otherwise use all users
		const dataToExport = search.trim() ? filteredData : allUsers;

		// Copy and stringify
		const parsedData = dataToExport.map((item) => ({
			id: item.id,
			name: item.name,
			lastName: item.lastName,
			email: item.email,
			os: item.os || '',
			level: item.level ? `(${item.level.id}) ${item.level.name}` : '',
			wepoints: item.totalPoints || '0',
			gender: item.gender ? item.gender.name : '',
			userType: item.userType ? item.userType.name : '',
			createdAt: item.createdAt ? dayjs(new Date(item.createdAt)).format('DD-MM-YYYY HH:mm') : '',
			lastLoginAt: item.lastLoginAt ? dayjs(new Date(item.lastLoginAt)).format('DD-MM-YYYY HH:mm') : '',
		}));

		// Parse and convert to CSV
		const csv = Papa.unparse(parsedData, { header: true });

		// Generate file
		const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8' });

		// Generate filename based on search
		const filename = search.trim() ? `users_filtered_${search}.csv` : 'users.csv';

		// Save file
		saveAs(blob, filename);
	};

	// Listen to page changes
	useEffect(() => {
		if (isLoading) return;

		if (search.trim()) return;

		getUsersList();

		//eslint-disable-next-line
	}, [page, search]);

	// Helper to apply points delta
	const applyPointsDelta = (user, delta) => ({
		...user,
		totalPoints: (user.totalPoints ?? 0) + delta,
	});

	// Handle save points
	const handleSavePoints = () => {
		setIsSaving(true);
		const values = form.values;
		const finalPoints =
			parseInt(values.points, 10) * (pointsMode === "descontar" ? -1 : 1);
	
		updateUserPoints(
			selectedUserId,
			{
				message: values.message,
				points: finalPoints,
			},
			() => {
				notifications.show({
					title: "Éxito",
					message: "Puntos actualizados correctamente",
					color: "green",
				});
	
				setOpened(false);
				setIsSaving(false);
				form.reset();
				setPointsMode("sumar");
	
				// 1) Update local states
				setAllUsers((prevAll) =>
					prevAll.map((u) =>
						u.id === selectedUserId ? applyPointsDelta(u, finalPoints) : u
					)
				);
	
				setUsers((prevUsers) =>
					prevUsers.map((u) =>
						u.id === selectedUserId ? applyPointsDelta(u, finalPoints) : u
					)
				);
	
				if (search.trim()) {
					setFilteredData((prevFiltered) =>
						prevFiltered.map((u) =>
							u.id === selectedUserId ? applyPointsDelta(u, finalPoints) : u
						)
					);
				}
	
				// 2) Refetch in background
				setTimeout(() => {
					getUsers(`?limit=9999&offset=0`, (response) => {
						const fresh = response.data.data;
						setAllUsers(fresh);
	
						// Reapply search if active
						if (search.trim()) {
							const query = search.toLowerCase().trim();
							const filtered = fresh.filter((item) =>
								Object.keys(fresh[0] || {}).some((key) =>
									String(item[key] ?? '').toLowerCase().includes(query)
								)
							);
							setFilteredData(filtered);
						} else {
							// Update visible page only if no search
							setUsers(fresh.slice((page - 1) * limit, page * limit));
						}
					});
				}, 0);
			},
			() => {
				notifications.show({
					title: "Error",
					message: "Hubo un error",
					color: "red",
				});
				setIsSaving(false);
			}
		);
	};

	useEffect(() => {
		if (!opened) {
		  form.reset();
		  setPointsMode('sumar');
		  setSelectedUserId(null);
		}
	}, [opened]);	

	return (
	<>
	<Modal
	  opened={opened}
	  onClose={() => setOpened(false)}
	  title="+/- Wepoints"
	  centered
	  size="lg"
	>
  		<Radio.Group
  		  value={pointsMode}
  		  onChange={setPointsMode}
  		  name="wepoints-type"
  		>
  		  <Radio value="sumar" label="Sumar" />
  		  <Radio value="descontar" label="Descontar" mt="xs" />
  		</Radio.Group>

		<TextInput
			label="Cantidad de puntos"
			placeholder="0"
			mt="md"
			type="number"
			{...form.getInputProps('points')}
		/>

		<TextInput
		  label="Asunto"
		  placeholder="Asunto del mensaje..."
		  mt="md"
		  {...form.getInputProps('message.subject')}
		/>

		<TextInput
		  label="Descripción"
		  placeholder="Descripción del mensaje..."
		  mt="md"
		  multiline
		  minRows={3}
		  {...form.getInputProps('message.body')}
		/>
		<Flex justify="space-between" mt="xl">
		    <Button variant="light" color="red" onClick={() => setOpened(false)}>
		      Cancelar
		    </Button>
		    <Button color="#4A0382" onClick={handleSavePoints} disabled={isSaving}>
		      Guardar
		    </Button>
		</Flex>
	</Modal>
		<Box className="mainContainer">
			{/* Content */}
			<Flex
				direction="column"
				justify="space-between"
				className={classes.container}
			>
				{/* Title */}
				<Title mb="xl">
					Usuarios
				</Title>
				{/* Group */}
				<Flex direction="column">
					{/* Top */}
					<Flex mb="md">
						{/* Download CSV */}
						<Button
							onClick={convertToCSV}
							variant="filled"
							leftIcon={<IconDownload size={18} />}
						>
							Descargar CSV
						</Button>
						{/* Search */}
						<TextInput
							placeholder="Buscar usuarios"
							icon={<IconSearch size="0.9rem" stroke={1.5} />}
							value={search}
							onChange={handleSearchChange}
							ml="sm"
							sx={{
								width: '100%',
								maxWidth: 350
							}}
							variant="filled"
						/>
					</Flex>
					{/* Table */}
					<ScrollArea>
						{isLoading ?
							<>
								<Skeleton height={40} radius="sm" />
								<Skeleton height={32} mt={12} radius="sm" />
								<Skeleton height={32} mt={12} radius="sm" />
								<Skeleton height={32} mt={12} radius="sm" />
								<Skeleton height={32} mt={12} radius="sm" />
							</>
							:
							<Table
								highlightOnHover
								sx={{ flex: 2 }}
							>
								<thead>{ths}</thead>
								<tbody>{rows.length > 0 ? (
									rows
								) : (
									<tr>
										<td colSpan={3}>
											<Text
												weight={500}
												align="center"
												my="xl"
												color="dimmed"
											>
												Sin resultados
											</Text>
										</td>
									</tr>
								)}</tbody>
							</Table>
						}
					</ScrollArea>
				</Flex>
				{/* Pages */}
				<Center sx={{ marginTop: 20 }}>
					<Pagination
						total={search.trim()
							? Math.ceil(filteredData.length / limit)
							: Math.ceil(total / limit)
						}
						siblings={1}
						defaultValue={1}
						withEdges
						onChange={setPage}
						value={page}
					/>
				</Center>
			</Flex>
		</Box>
		</>
	);

}

export default Users;
